import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getCountFromServer,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { db, auth, isFirebaseConfigured } from "./config";

const cloudinaryCloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "";
const cloudinaryUploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "";
const cloudinaryMaterialUploadPreset = process.env.REACT_APP_CLOUDINARY_MATERIAL_UPLOAD_PRESET || cloudinaryUploadPreset;
const cloudinaryCertificateUploadPreset = process.env.REACT_APP_CLOUDINARY_CERTIFICATE_UPLOAD_PRESET || "";
const emailjsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
const emailjsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "";
const emailjsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "";

const makeActivationToken = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const uploadStudentPhoto = async (file, applicationNumber) => {
  if (!cloudinaryCloudName || !cloudinaryUploadPreset) {
    throw new Error("Cloudinary is not configured. Add REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET to frontend/.env.");
  }
  const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
  if (!allowedTypes.has(file?.type)) throw new Error("Please upload a JPEG, PNG, or WebP image.");
  if (file.size > 5 * 1024 * 1024) throw new Error("Passport photo must be 5MB or smaller.");

  const body = new FormData();
  body.append("file", file);
  body.append("upload_preset", cloudinaryUploadPreset);
  body.append("folder", `kian-masterclass/student-photos/${applicationNumber}`);

  const response = await fetch(`https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudinaryCloudName)}/image/upload`, {
    method: "POST",
    body,
  });
  const data = await response.json();
  if (!response.ok || !data.secure_url) {
    throw new Error(data?.error?.message || "Could not upload passport photo.");
  }
  return data.secure_url;
};


const uploadCloudinaryFile = async (file, { folder, preset, maxBytes, allowedExtensions, label }) => {
  if (!cloudinaryCloudName || !preset) {
    throw new Error(`${label} upload is not configured.`);
  }
  if (!file) throw new Error(`Please choose a ${label.toLowerCase()} file.`);
  if (file.size > maxBytes) throw new Error(`${label} must be ${Math.round(maxBytes / (1024 * 1024))}MB or smaller.`);

  const extension = String(file.name || "").split(".").pop().toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    throw new Error(`Unsupported ${label.toLowerCase()} format. Allowed: ${allowedExtensions.join(", ")}.`);
  }

  const body = new FormData();
  body.append("file", file);
  body.append("upload_preset", preset);
  body.append("folder", folder);

  const response = await fetch(`https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudinaryCloudName)}/auto/upload`, {
    method: "POST",
    body,
  });
  const data = await response.json();
  if (!response.ok || !data.secure_url || !String(data.secure_url).startsWith("https://res.cloudinary.com/")) {
    throw new Error(data?.error?.message || `Could not upload the ${label.toLowerCase()}.`);
  }
  return {
    secureUrl: data.secure_url,
    publicId: data.public_id || "",
    bytes: Number(data.bytes || file.size || 0),
    format: data.format || extension,
  };
};

const MATERIAL_EXTENSIONS = ["pdf", "doc", "docx", "ppt", "pptx", "txt", "zip", "csv", "xlsx", "xls", "png", "jpg", "jpeg", "webp"];
const CERTIFICATE_EXTENSIONS = ["pdf", "png", "jpg", "jpeg", "webp"];

const makePublicId = (prefix = "KT-CYBER") => `${prefix}-${new Date().getFullYear()}-${Date.now().toString(36).toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`;


const sendActivationEmail = async (registration) => {
  if (!emailjsServiceId || !emailjsTemplateId || !emailjsPublicKey) {
    throw new Error("EmailJS activation email is not configured.");
  }
  const activationUrl = `${window.location.origin}/activate?token=${encodeURIComponent(registration.activationToken)}`;
  const payload = {
    service_id: emailjsServiceId,
    template_id: emailjsTemplateId,
    user_id: emailjsPublicKey,
    template_params: {
      to_email: registration.email,
      to_name: registration.fullName,
      student_name: registration.fullName,
      student_id: registration.studentId,
      application_number: registration.applicationNumber || "",
      activation_code: registration.activationToken,
      activation_link: activationUrl,
      activation_url: activationUrl,
      portal_url: window.location.origin,
      reply_to: "hello@kiantechnologies.in",
    },
  };
  const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  if (!response.ok) throw new Error((await response.text()) || `EmailJS returned ${response.status}.`);
  return true;
};

export const formatStudentId = (num) => {
  const padded = String(num).padStart(4, "0");
  return `KIAN-CYBER-${padded}`;
};

export const authService = {
  async login(email, password) {
    if (!auth) throw new Error("Firebase Auth is not initialized. Please configure your Firebase credentials.");
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
    const user = userCredential.user;
    
    let userProfile = null;
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userDocRef);
      if (userSnap.exists()) {
        userProfile = userSnap.data();
      } else {
        const studentQuery = query(collection(db, "students"), where("email", "==", user.email.toLowerCase()));
        const studentSnap = await getDocs(studentQuery);
        if (!studentSnap.empty) {
          userProfile = { ...studentSnap.docs[0].data(), role: "student" };
        }
      }
    } catch (err) {
      console.warn("Could not fetch user profile from Firestore:", err);
    }
    
    return { user, profile: userProfile };
  },

  async logout() {
    if (!auth) return;
    await signOut(auth);
  },

  async sendPasswordReset(email) {
    if (!auth) throw new Error("Firebase Auth is not initialized.");
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) throw new Error("Please enter your registered email address.");
    // Use Firebase's native reset flow without a custom continue URL. This keeps
    // the reset email on Firebase's standard action handler and avoids failures
    // caused by an un-authorized continue URL/domain.
    await sendPasswordResetEmail(auth, normalizedEmail);
  },

  async getUserRole(uid, email) {
    if (!db) return null;
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      if (email) {
        const q = query(collection(db, "students"), where("email", "==", email.toLowerCase()));
        const snap = await getDocs(q);
        if (!snap.empty) {
          return { ...snap.docs[0].data(), role: "student" };
        }
      }
    } catch (e) {
      console.error("Error fetching user role:", e);
    }
    return null;
  }
};

export const registrationService = {
  async submitRegistration(data, photoFile) {
    if (!db) throw new Error("Firebase Firestore is not initialized.");

    const tempAppId = "REG-" + Date.now().toString(36).toUpperCase() + "-" + Math.floor(1000 + Math.random() * 9000);
    const activationToken = makeActivationToken();
    const photoUrl = await uploadStudentPhoto(photoFile, tempAppId);

    const regRef = doc(collection(db, "registrations"));
    const tokenRef = doc(db, "activationTokens", activationToken);
    const profile = {
      fullName: data.fullName.trim(),
      email: data.email.trim().toLowerCase(),
      mobile: data.mobile.trim(),
      city: data.city.trim(),
      state: data.state ? data.state.trim() : "",
      college: data.college.trim(),
      course: data.course.trim(),
      branch: data.branch ? data.branch.trim() : "",
      yearSemester: data.yearSemester.trim(),
      graduationYear: data.graduationYear ? data.graduationYear.trim() : "",
      experience: data.experience,
      interestedArea: data.interestedArea,
      statementOfPurpose: data.statementOfPurpose.trim(),
      hearAbout: data.hearAbout ? data.hearAbout.trim() : "",
      attendRegularly: Boolean(data.attendRegularly),
      photoUrl,
    };

    const regData = {
      applicationNumber: tempAppId,
      ...profile,
      status: "pending",
      activationToken,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // The token document contains only activation metadata/profile needed by the applicant.
    // The token itself is high entropy and is shown once on the success screen.
    const tokenData = {
      email: profile.email,
      activationToken,
      registrationId: regRef.id,
      applicationNumber: tempAppId,
      status: "pending",
      studentId: "",
      activatedUid: "",
      profile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const batch = writeBatch(db);
    batch.set(regRef, regData);
    batch.set(tokenRef, tokenData);
    await batch.commit();

    return { id: regRef.id, applicationNumber: tempAppId, activationToken, ...regData };
  },

  async getRegistrations(statusFilter = "all") {
    if (!db) return [];
    let q;
    if (statusFilter === "all") {
      q = query(collection(db, "registrations"), orderBy("createdAt", "desc"));
    } else {
      q = query(collection(db, "registrations"), where("status", "==", statusFilter), orderBy("createdAt", "desc"));
    }
    const snap = await getDocs(q);
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  async getRegistrationById(id) {
    if (!db) return null;
    const snap = await getDoc(doc(db, "registrations", id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async approveRegistration(regId) {
    if (!db) throw new Error("Firestore not initialized.");
    const regRef = doc(db, "registrations", regId);
    const regSnap = await getDoc(regRef);
    if (!regSnap.exists()) throw new Error("Registration not found.");
    const reg = regSnap.data();
    if (reg.status === "approved" && reg.studentId) {
      return { studentId: reg.studentId, alreadyApproved: true };
    }
    if (reg.status !== "pending") throw new Error(`This application is already ${reg.status}.`);
    if (!reg.activationToken) throw new Error("Activation token is missing for this registration.");

    const tokenRef = doc(db, "activationTokens", reg.activationToken);
    const result = await runTransaction(db, async (transaction) => {
      const freshRegSnap = await transaction.get(regRef);
      const tokenSnap = await transaction.get(tokenRef);
      if (!freshRegSnap.exists()) throw new Error("Registration no longer exists.");
      const freshReg = freshRegSnap.data();
      if (freshReg.status === "approved" && freshReg.studentId) return { studentId: freshReg.studentId, alreadyApproved: true };
      if (freshReg.status !== "pending") throw new Error(`This application is already ${freshReg.status}.`);
      if (!tokenSnap.exists()) throw new Error("Activation token record is missing.");

      const counterRef = doc(db, "system", "counters");
      const counterSnap = await transaction.get(counterRef);
      const nextNumber = Number(counterSnap.exists() ? counterSnap.data().studentSequence || 0 : 0) + 1;
      const studentId = formatStudentId(nextNumber);

      transaction.set(counterRef, { studentSequence: nextNumber, updatedAt: serverTimestamp() }, { merge: true });
      transaction.update(regRef, { status: "approved", studentId, approvedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      transaction.update(tokenRef, { status: "approved", studentId, updatedAt: serverTimestamp() });
      return { studentId, alreadyApproved: false };
    });

    let emailSent = false;
    let emailError = "";
    try {
      const latest = await this.getRegistrationById(regId);
      if (latest) { await sendActivationEmail(latest); emailSent = true; }
    } catch (emailErr) {
      emailError = emailErr?.message || "Activation email could not be sent.";
      console.warn("Activation email failed:", emailErr);
    }
    return { ...result, emailSent, emailError };
  },

  async resendActivationEmail(regId) {
    const registration = await this.getRegistrationById(regId);
    if (!registration) throw new Error("Registration not found.");
    if (registration.status !== "approved" || !registration.studentId) throw new Error("Approve the application before sending the activation email.");
    await sendActivationEmail(registration);
    return true;
  },

  async rejectRegistration(regId, rejectionReason = "Does not meet entry criteria") {
    if (!db) throw new Error("Firestore not initialized.");
    await updateDoc(doc(db, "registrations", regId), {
      status: "rejected",
      rejectionReason,
      rejectedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return true;
  }
};

export const studentService = {
  async getStudents() {
    if (!db) return [];
    const q = query(collection(db, "students"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  async getStudentByUid(uid) {
    if (!db || !uid) return null;
    const snap = await getDoc(doc(db, "students", uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async getStudentByEmail(email) {
    if (!db || !email) return null;
    const q = query(collection(db, "students"), where("email", "==", email.toLowerCase()));
    const snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    return null;
  },

  async migrateStudentIdsToKianFormat() {
    if (!db) throw new Error("Firestore not initialized.");
    const studentsSnap = await getDocs(collection(db, "students"));
    const students = studentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    let changed = 0;
    const idMap = new Map();

    const batch = writeBatch(db);
    for (const student of students) {
      const current = String(student.studentId || "");
      const match = current.match(/(?:KT-CYBER-2026|KIAN-CYBER)-(\d{1,})$/i);
      if (!match) continue;
      const nextId = formatStudentId(Number(match[1]));
      if (current === nextId) continue;
      idMap.set(current, nextId);
      batch.update(doc(db, "students", student.id), { studentId: nextId, updatedAt: serverTimestamp() });
      batch.update(doc(db, "users", student.id), { studentId: nextId, updatedAt: serverTimestamp() });
      if (student.registrationId) batch.update(doc(db, "registrations", student.registrationId), { studentId: nextId, updatedAt: serverTimestamp() });
      if (student.activationToken) batch.update(doc(db, "activationTokens", student.activationToken), { studentId: nextId, updatedAt: serverTimestamp() });
      changed += 1;
    }

    // Attendance document IDs historically include the old Student ID. Re-key those
    // records so the ID change does not orphan attendance history.
    if (idMap.size) {
      const attendanceSnap = await getDocs(collection(db, "attendance"));
      attendanceSnap.docs.forEach((d) => {
        const data = d.data();
        const oldId = String(data.studentId || "");
        const nextId = idMap.get(oldId);
        if (!nextId) return;
        const dayNumber = Number(data.dayNumber || 0);
        const nextRef = doc(db, "attendance", `day_${dayNumber}_${nextId}`);
        batch.set(nextRef, { ...data, studentId: nextId, updatedAt: serverTimestamp() }, { merge: true });
        batch.delete(d.ref);
      });
    }

    if (changed) await batch.commit();
    return { changed };
  },

  async toggleStudentStatus(studentUid, currentStatus) {
    if (!db) throw new Error("Firestore not initialized.");
    const newStatus = currentStatus === "active" ? "deactivated" : "active";
    const studentRef = doc(db, "students", studentUid);
    const userRef = doc(db, "users", studentUid);
    const batch = writeBatch(db);
    batch.update(studentRef, { status: newStatus, updatedAt: serverTimestamp() });
    batch.update(userRef, { status: newStatus, updatedAt: serverTimestamp() });
    await batch.commit();
    return newStatus;
  },

  async activateAccount(activationToken, email, password) {
    if (!db || !auth) throw new Error("Firebase is not configured.");
    const normalizedEmail = email.trim().toLowerCase();
    const tokenRef = doc(db, "activationTokens", activationToken.trim());
    const tokenSnap = await getDoc(tokenRef);
    if (!tokenSnap.exists()) throw new Error("Invalid activation code.");
    const token = tokenSnap.data();
    if (token.status !== "approved") throw new Error("Your application has not been approved yet.");
    if (token.activatedUid) throw new Error("This activation code has already been used. Please log in instead.");
    if (token.email !== normalizedEmail) throw new Error("Email does not match the approved application.");
    if (!token.studentId || !token.profile) throw new Error("Approval data is incomplete. Please contact the coordinator.");

    const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    const user = credential.user;
    try {
      const userRef = doc(db, "users", user.uid);
      const studentRef = doc(db, "students", user.uid);
      const batch = writeBatch(db);
      batch.update(tokenRef, { activatedUid: user.uid, status: "activated", activatedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      batch.set(userRef, {
        uid: user.uid,
        email: normalizedEmail,
        fullName: token.profile.fullName,
        role: "student",
        status: "active",
        studentId: token.studentId,
        registrationId: token.registrationId,
        activationToken: activationToken.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      batch.set(studentRef, {
        ...token.profile,
        uid: user.uid,
        role: "student",
        status: "active",
        studentId: token.studentId,
        registrationId: token.registrationId,
        activationToken: activationToken.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
	return { user, studentId: token.studentId };
    } catch (err) {
      try { await user.delete(); } catch (deleteErr) { console.warn("Could not roll back Auth account:", deleteErr?.message); }
      throw err;
    }
  }
};

export const attendanceService = {
  async getStudentAttendance(studentUid) {
    if (!db || !studentUid) return [];
    const q = query(
      collection(db, "attendance"),
      where("studentUid", "==", studentUid)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => Number(a.dayNumber || 0) - Number(b.dayNumber || 0));
  },

  async getAttendanceByDateAndDay(dateStr, dayNumber) {
    if (!db) return [];
    const q = query(
      collection(db, "attendance"),
      where("dayNumber", "==", Number(dayNumber))
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async saveBatchAttendance(dayNumber, dateStr, topic, records) {
    if (!db) throw new Error("Firestore not initialized.");
    const batchPromises = records.map(async (record) => {
      if (!record.studentUid) throw new Error("Attendance record is missing student UID.");
      const attendanceDocId = `day_${dayNumber}_${record.studentId}`;
      const docRef = doc(db, "attendance", attendanceDocId);
      return setDoc(docRef, {
        dayNumber: safeDay,
        date: dateStr,
        topic: topic || `Day ${dayNumber} Masterclass Session`,
        studentId: record.studentId,
        studentName: record.studentName,
        studentUid: record.studentUid,
        studentEmail: record.studentEmail.trim().toLowerCase(),
        status: record.status,
        markedAt: serverTimestamp(),
      }, { merge: true });
    });

    await Promise.all(batchPromises);
    return true;
  }
};

export const materialService = {
  async getMaterials(publishedOnly = true) {
    if (!db) return [];
    // Avoid a composite Firestore index dependency. Students only need published
    // resources; sort the small result set in the browser by day/title.
    const q = publishedOnly
      ? query(collection(db, "materials"), where("published", "==", true))
      : query(collection(db, "materials"));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => Number(a.dayNumber || 0) - Number(b.dayNumber || 0) || String(a.title || "").localeCompare(String(b.title || "")));
  },

  async uploadMaterial({ title, description, dayNumber, file, published = true }) {
    if (!db) throw new Error("Firestore not initialized.");
    if (!title?.trim()) throw new Error("Please provide a title.");
    if (!file) throw new Error("Please choose a study material file.");

    const safeDay = Number(dayNumber);
    if (!Number.isInteger(safeDay) || safeDay < 1 || safeDay > 15) throw new Error("Day number must be between 1 and 15.");
    const uploaded = await uploadCloudinaryFile(file, { folder: `kian-masterclass/materials/day-${safeDay}`, preset: cloudinaryMaterialUploadPreset, maxBytes: 25 * 1024 * 1024, allowedExtensions: MATERIAL_EXTENSIONS, label: "Study material" });
    const docRef = await addDoc(collection(db, "materials"), {
      title: title.trim(),
      description: description ? description.trim() : "",
      dayNumber: Number(dayNumber),
      fileUrl: uploaded.secureUrl,
      fileName: file.name,
      fileSize: uploaded.bytes,
      fileFormat: uploaded.format,
      cloudinaryPublicId: uploaded.publicId,
      published: Boolean(published),
      createdAt: serverTimestamp(),
    });

    return { id: docRef.id, title: title.trim(), dayNumber: safeDay, fileUrl: uploaded.secureUrl, fileName: file.name, published };
  },

  async deleteMaterial(materialId) {
    if (!db) throw new Error("Firestore not initialized.");
    // Cloudinary deletion requires an authenticated server-side API secret.
    // On Firebase Spark we intentionally delete the Firestore record only.
    await deleteDoc(doc(db, "materials", materialId));
    return true;
  }
};


export const certificateService = {
  async getStudentCertificates(studentUid) {
    if (!db || !studentUid) return [];
    const q = query(collection(db, "certificates"), where("studentUid", "==", studentUid));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(b.issueDate || "").localeCompare(String(a.issueDate || "")) || String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
  },

  async getStudentCertificate(studentUid) {
    const list = await this.getStudentCertificates(studentUid);
    return list[0] || null;
  },

  async getCertificateById(certificateId) {
    if (!db || !certificateId) return null;
    const snap = await getDoc(doc(db, "certificates", certificateId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async getIssuedCertificates() {
    if (!db) return [];
    const snap = await getDocs(collection(db, "certificates"));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(b.issueDate || "").localeCompare(String(a.issueDate || "")));
  },

  async uploadCertificate({ studentUid, studentId, studentName, title, certificateType, issueDate, file }) {
    if (!db) throw new Error("Firestore not initialized.");
    if (!studentUid || !studentId || !studentName) throw new Error("Please select a valid student.");
    if (!title?.trim()) throw new Error("Please provide a certificate/document title.");
    if (!file) throw new Error("Please choose the certificate file.");

    const uploaded = await uploadCloudinaryFile(file, { folder: `kian-masterclass/certificates/${studentId}`, preset: cloudinaryCertificateUploadPreset, maxBytes: 10 * 1024 * 1024, allowedExtensions: CERTIFICATE_EXTENSIONS, label: "Certificate/document" });
    const certificateId = makePublicId("KIAN-CERT");
    const verificationCode = makePublicId("VERIFY");
    const data = {
      certificateId,
      verificationCode,
      studentUid,
      studentId,
      studentName,
      title: title.trim(),
      certificateType: (certificateType || "Certificate").trim(),
      issueDate: issueDate || new Date().toISOString().slice(0, 10),
      status: "valid",
      fileUrl: uploaded.secureUrl,
      fileName: file.name,
      fileSize: uploaded.bytes,
      fileFormat: uploaded.format,
      cloudinaryPublicId: uploaded.publicId,
      createdAt: serverTimestamp(),
    };
    await setDoc(doc(db, "certificates", certificateId), data);
    return { id: certificateId, ...data };
  },

  async revokeCertificate(certificateId, reason = "Document revoked by institute") {
    if (!db) throw new Error("Firestore not initialized.");
    await updateDoc(doc(db, "certificates", certificateId), {
      status: "revoked",
      revocationReason: reason,
      revokedAt: serverTimestamp(),
    });
    return true;
  },

  async deleteCertificate(certificateId) {
    if (!db) throw new Error("Firestore not initialized.");
    await deleteDoc(doc(db, "certificates", certificateId));
    return true;
  },
};

export const scheduleService = {
  async getSchedules() {
    if (!db) return [];
    const q = query(collection(db, "schedules"), orderBy("dayNumber", "asc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async saveScheduleDay(scheduleData) {
    if (!db) throw new Error("Firestore not initialized.");
    const data = {
      dayNumber: Number(scheduleData.dayNumber),
      date: scheduleData.date,
      topic: scheduleData.topic.trim(),
      instructor: scheduleData.instructor.trim(),
      startTime: scheduleData.startTime.trim(),
      endTime: scheduleData.endTime.trim(),
      description: scheduleData.description ? scheduleData.description.trim() : "",
      updatedAt: serverTimestamp(),
    };

    if (scheduleData.id) {
      await updateDoc(doc(db, "schedules", scheduleData.id), data);
      return { id: scheduleData.id, ...data };
    } else {
      data.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, "schedules"), data);
      return { id: docRef.id, ...data };
    }
  },

  async deleteScheduleDay(id) {
    if (!db) throw new Error("Firestore not initialized.");
    await deleteDoc(doc(db, "schedules", id));
    return true;
  }
};

export const announcementService = {
  async getAnnouncements() {
    if (!db) return [];
    const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async createAnnouncement(data) {
    if (!db) throw new Error("Firestore not initialized.");
    const docRef = await addDoc(collection(db, "announcements"), {
      title: data.title.trim(),
      content: data.content.trim(),
      priority: data.priority || "normal",
      author: data.author || "Masterclass Coordinator",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: docRef.id, ...data };
  },

  async updateAnnouncement(id, data) {
    if (!db) throw new Error("Firestore not initialized.");
    await updateDoc(doc(db, "announcements", id), {
      title: data.title.trim(),
      content: data.content.trim(),
      priority: data.priority || "normal",
      updatedAt: serverTimestamp(),
    });
    return true;
  },

  async deleteAnnouncement(id) {
    if (!db) throw new Error("Firestore not initialized.");
    await deleteDoc(doc(db, "announcements", id));
    return true;
  }
};

export const dashboardService = {
  async getAdminStats() {
    if (!db) {
      return {
        totalRegistrations: 0,
        pendingApplications: 0,
        approvedStudents: 0,
        rejectedApplications: 0,
      };
    }

    try {
      const regCol = collection(db, "registrations");
      const [allSnap, pendingSnap, approvedSnap, rejectedSnap] = await Promise.all([
        getCountFromServer(regCol),
        getCountFromServer(query(regCol, where("status", "==", "pending"))),
        getCountFromServer(query(regCol, where("status", "==", "approved"))),
        getCountFromServer(query(regCol, where("status", "==", "rejected"))),
      ]);

      return {
        totalRegistrations: allSnap.data().count,
        pendingApplications: pendingSnap.data().count,
        approvedStudents: approvedSnap.data().count,
        rejectedApplications: rejectedSnap.data().count,
      };
    } catch (e) {
      console.warn("Could not get count from server, falling back to query snapshot:", e);
      const allDocs = await getDocs(collection(db, "registrations"));
      const list = allDocs.docs.map(d => d.data());
      return {
        totalRegistrations: list.length,
        pendingApplications: list.filter(r => r.status === "pending").length,
        approvedStudents: list.filter(r => r.status === "approved").length,
        rejectedApplications: list.filter(r => r.status === "rejected").length,
      };
    }
  }
};
